import {
  ARCHIVE_STATUS_CHILDREN,
  ARCHIVE_STATUS_ENTRY,
  MEDIA_ENTRY_APPLICATION,
  MEDIA_ENTRY_CONTENT_GROUP,
  MEDIA_ENTRY_FOLDER,
  MEDIA_ENTRY_USER_CONTENT,
  MEDIA_ENTRY_WEBPAGE,
  MEDIA_LIBRARY_URL,
  VALID_IMAGE_MIMETYPES,
  VALID_MIMETYPES,
  VALID_VIDEO_MIMETYPES,
} from "fleet/constant/media-library";
import { SpotPreview } from "fleet/view/strategy-v2/spot-preview";
import flattenDeep from "lodash/flattenDeep";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import moment from "moment";
import React from "react";
import { getById } from "redux-store";
import * as App from "redux-store/applications/selectors";
import { priorityMerge } from "util/object";
import { utcToLocal } from "util/time";

const MEDIA_ENTRY_SHAPE = Object.freeze({
  _rowNum: null,
  accountId: null,
  archived: null,
  available: null,
  conflictsWith: null,
  content: null,
  displayName: null,
  duration: null,
  folderId: null,
  folderName: null,
  friendlyType: null,
  id: null,
  installed: null,
  mimeType: null,
  name: null,
  numberOfSpots: null,
  tag: null,
  type: null,
  uploadDate: null,
  url: null,
  userId: null,
});

function createMediaEntry(props) {
  return priorityMerge({}, MEDIA_ENTRY_SHAPE, props);
}

function isUserContent(type) {
  return type === MEDIA_ENTRY_USER_CONTENT;
}

function isImage(mimeType) {
  return VALID_IMAGE_MIMETYPES.includes(mimeType);
}

function isVideo(mimeType) {
  return VALID_VIDEO_MIMETYPES.includes(mimeType);
}

function isValidMimeType(mimeType) {
  return VALID_MIMETYPES.includes(mimeType);
}

function isContentFolder(type) {
  return type === MEDIA_ENTRY_FOLDER;
}

function getFolderUrl(folderId) {
  return `${MEDIA_LIBRARY_URL}/folders/${folderId}`;
}

function formatUploadDate(_, media) {
  const date = media.uploadDate;
  return utcToLocal(date);
}

function formatNameWithFolderName(name, mediaEntry) {
  const { folderName, type } = mediaEntry;

  if (!isContentFolder(type) && folderName) {
    return `${folderName}/${name}`;
  }

  return name;
}

function formatPreview(url, mediaEntry) {
  const { type, mimeType, numberOfSpots } = mediaEntry;

  return (
    <SpotPreview
      className="media-icon"
      mimeType={mimeType}
      numberOfSpots={numberOfSpots}
      type={type}
      url={url}
    />
  );
}

function getCreativeCategories(userContents = [], strategies = []) {
  const categories = new Set();
  userContents.forEach((c) => {
    if (c.tag && !c.archived) {
      categories.add(c.tag);
    }
  });

  const spots = flattenDeep(
    strategies.map((s) => {
      return get(s, "strategy.priorities", []);
    }),
  );

  spots.forEach((spot) => {
    const { options = [] } = spot;
    let { conflicts_with: allConflictsWith = "" } = spot;

    // Get categories from content option conflicts with
    if (options.length) {
      allConflictsWith = options
        .map((o) => {
          return o.conflicts_with || "";
        })
        .join(",");
    }

    allConflictsWith
      .split(",")
      .filter((cwCat) => cwCat)
      .forEach((cwc) => {
        categories.add(cwc);
      });
  });

  const { lastResult } = getCreativeCategories;
  const nextCategories = [...categories].sort();

  if (isEqual(lastResult, nextCategories)) {
    return lastResult;
  }

  getCreativeCategories.lastResult = nextCategories;
  return nextCategories;
}

/**
 * Takes arrays of normalized data and transforms them into an array
 * of MediaEntries.
 *
 * @typedef {Object} MediaEntry
 *
 * @param {Object} stores - an object containing stores:
 *   applications{}, contentGroups{}, folders{}, userContents{}.
 * @param {Array} sources - An array of exactly
 *   [applications[], contentGroups[], folders[], userContents[]]
 *
 * @return {MediaEntry[]} - an array of media entries.
 */
function getMediaEntries(stores, sources) {
  const [applications, contentGroups, folders, userContents] = sources;

  const dnApplications = applications
    .filter(App.installed)
    .map(applicationToMediaEntry);
  const dnUserContents = userContents.map(
    userContentToMediaEntry.bind(null, stores),
  );
  const dnContentGroups = contentGroups.map(
    contentGroupToMediaEntry.bind(null, stores),
  );
  const dnFolders = folders.map(contentFolderToMediaEntry);

  return [
    ...dnApplications,
    ...dnUserContents,
    ...dnContentGroups,
    ...dnFolders,
  ];
}

function sortByUploadDate(a, b, order) {
  const aDate = moment(a);
  const bDate = moment(b);
  if (aDate.isSame(bDate)) {
    return 0;
  }

  const dir = order === "desc" ? -1 : 1;
  const sortOrder = aDate > bDate ? 1 : -1;
  return dir * sortOrder;
}

function getFriendlyType(type, mimeType = null) {
  if (isUserContent(type)) {
    if (isVideo(mimeType)) {
      return "Video";
    }

    if (isImage(mimeType)) {
      return "Image";
    }

    return "Unknown";
  }

  switch (type) {
    case MEDIA_ENTRY_APPLICATION:
      return "Application";
    case MEDIA_ENTRY_CONTENT_GROUP:
      return "Media Group";
    case MEDIA_ENTRY_FOLDER:
      return "Folder";
    case MEDIA_ENTRY_WEBPAGE:
      return "Web Page";
    default:
      return "Unknown";
  }
}

function strategyLevelAppToMediaEntry(stores, appId, index) {
  const { applications } = stores;

  const app = App.searchAllApps(applications, appId);

  return applicationToMediaEntry(app, index);
}

function strategyLevelWebpageToMediaEntry(webpage, index) {
  const { duration, name, url } = webpage;

  return createMediaEntry({
    duration,
    name,
    url,
    _rowNum: index,
    friendlyType: getFriendlyType(MEDIA_ENTRY_WEBPAGE),
    type: MEDIA_ENTRY_WEBPAGE,
  });
}

function strategyLevelUCToMediaEntry(stores, strategyUC, index) {
  const { userContents } = stores;
  const {
    id: rawId,
    contentId,
    conflicts_with: conflictsWith,
    duration: cgDuration,
  } = strategyUC;

  // We need to fix strategy level data structure to contain id.
  // In the mean time read either.
  const id = rawId || contentId;
  const storeUserContent = getById(userContents, id);

  const mediaEntry = userContentToMediaEntry(stores, storeUserContent, index);
  const { mimeType, duration: ucDuration } = mediaEntry;
  const duration = isVideo(mimeType) ? ucDuration : cgDuration;

  return {
    ...mediaEntry,
    conflictsWith: conflictsWith ? conflictsWith.split(",") : null,
    duration,
  };
}

function strategyLevelCGToMediaEntry(stores, stratCG, index) {
  const { contentGroups } = stores;
  const { id: rawId, contentGroupId, options = [] } = stratCG;

  // TODO: (akaringal/jhohmann) COR-2030: We need to fix strategy level
  // data structure to contain id. In the mean time read either.
  const id = rawId || contentGroupId;
  const storeContentGroup = getById(contentGroups, id);
  const mediaEntry = contentGroupToMediaEntry(stores, storeContentGroup, index);
  const { content: storeContent } = mediaEntry;

  const content = storeContent.map((userContentME, idx) => {
    const { duration: contentDuration, mimeType } = userContentME;
    const contentOptions = options[idx] || {};
    const { conflicts_with: conflictsWith = "", duration: strategyDuration } =
      contentOptions;
    const { duration: contentGroupDuration } = storeContentGroup.content[idx];

    const duration = isVideo(mimeType)
      ? contentDuration
      : strategyDuration || contentGroupDuration || contentDuration;
    return {
      ...userContentME,
      duration,
      conflictsWith: conflictsWith ? conflictsWith.split(",") : null,
    };
  });

  return {
    ...mediaEntry,
    content,
  };
}

function applicationToMediaEntry(storeApp, index) {
  let entry = {
    _rowNum: index,
    friendlyType: getFriendlyType(MEDIA_ENTRY_APPLICATION),
    type: MEDIA_ENTRY_APPLICATION,
  };

  if (!storeApp) {
    return createMediaEntry(entry);
  }

  const { id } = storeApp;

  entry = {
    ...entry,
    id,
    displayName: App.name(storeApp),
    installed: App.installed(storeApp),
    name: App.name(storeApp),
    url: App.icon(storeApp),
    available: App.available(storeApp),
  };

  if (entry.installed) {
    const uploadDate = get(storeApp, "stable_version.change_date", null);
    Object.assign(entry, { uploadDate });
  }

  return createMediaEntry(entry);
}

function contentFolderToMediaEntry(storeCF, index) {
  const {
    archived,
    content_groups,
    contents,
    create_date: uploadDate,
    id,
    name,
  } = storeCF;
  return createMediaEntry({
    archived,
    id,
    name,
    uploadDate,
    _rowNum: index,
    // eslint-disable-next-line camelcase
    contents: [...contents, ...content_groups],
    displayName: name,
    friendlyType: getFriendlyType(MEDIA_ENTRY_FOLDER),
    type: MEDIA_ENTRY_FOLDER,
  });
}

function userContentToMediaEntry(stores, storeUC, index) {
  const { contentFolders } = stores;
  const {
    account_id: accountId,
    archived,
    duration,
    folder_id: folderId,
    id,
    name,
    tag,
    type: mimeType,
    upload_date: uploadDate,
    url,
    user_id: userId,
  } = storeUC;
  const folder = getById(contentFolders, folderId);
  const folderName = folder ? folder.name : null;

  // Underscore to indicate its a special property that doesn't
  // really come from the model.
  const entry = {
    accountId,
    archived,
    duration,
    folderId,
    folderName,
    id,
    mimeType,
    name,
    tag,
    uploadDate,
    url,
    userId,
    _rowNum: index,
    friendlyType: getFriendlyType(MEDIA_ENTRY_USER_CONTENT, mimeType),
    type: MEDIA_ENTRY_USER_CONTENT,
  };

  Object.assign(entry, {
    displayName: formatNameWithFolderName(name, entry),
  });

  return createMediaEntry(entry);
}

function contentGroupToMediaEntry(stores, storeCG, index) {
  const { contentFolders, userContents } = stores;

  const {
    account_id: accountId,
    archived,
    content: normContent,
    create_date: uploadDate,
    folder_id: folderId,
    id,
    name,
  } = storeCG;

  const folder = getById(contentFolders, folderId);
  const folderName = folder ? folder.name : null;

  // We need to denormalize the content
  // content is a different shape than user content from strategy.
  const denormContent = normContent.map((cgUC, idx) => {
    const storeUC = getById(userContents, cgUC.id) || {};
    const userContent = priorityMerge({}, storeUC, cgUC);

    return userContentToMediaEntry(stores, userContent, idx);
  });

  // Underscore to indicate its a special property that doesn't
  // really come from the model.
  const entry = {
    accountId,
    archived,
    folderName,
    folderId,
    id,
    name,
    uploadDate,
    _rowNum: index,
    friendlyType: getFriendlyType(MEDIA_ENTRY_CONTENT_GROUP),
    type: MEDIA_ENTRY_CONTENT_GROUP,
    content: denormContent,
    numberOfSpots: denormContent.length,
  };

  Object.assign(entry, {
    displayName: formatNameWithFolderName(name, entry),
  });

  return createMediaEntry(entry);
}

function getArchiveStatus(mediaEntry) {
  const { archived, content } = mediaEntry;

  if (archived) {
    return ARCHIVE_STATUS_ENTRY;
  }

  if (content && content.some((c) => c.archived)) {
    return ARCHIVE_STATUS_CHILDREN;
  }

  return null;
}

function webPageToMediaEntry(webPage) {
  const { name, url, duration: durationInSecs } = webPage;
  const duration = durationInSecs * 1000;
  const webPageProps = {
    duration,
    name,
    url,
    friendlyType: "Web Page",
    type: MEDIA_ENTRY_WEBPAGE,
  };

  return createMediaEntry(webPageProps);
}

function folderContentsToSpots(
  applications,
  userContents,
  contentGroups,
  folder,
) {
  const { content_groups: folderContentGroups, contents } = folder;

  const stores = {
    applications,
    userContents,
    contentGroups,
  };

  const cgSpots = folderContentGroups
    .filter((cg) => !cg.archived)
    .map(contentGroupToMediaEntry.bind(null, stores));
  const contentSpots = contents
    .filter((c) => !c.archived)
    .map(userContentToMediaEntry.bind(null, stores));

  return [...cgSpots, ...contentSpots];
}

function normalizeContentGroup(mediaEntry) {
  const { accountId, archived, content, uploadDate, folderId, id, name } =
    mediaEntry;

  const normalizedContent = content.map((uc) => {
    const { id, duration, mimeType: type, name, url } = uc;

    return {
      id,
      duration,
      type,
      name,
      url,
    };
  });

  return {
    account_id: accountId,
    archived,
    content: normalizedContent,
    create_date: uploadDate,
    folder_id: folderId,
    id,
    name,
  };
}

export {
  applicationToMediaEntry,
  contentGroupToMediaEntry,
  contentFolderToMediaEntry,
  createMediaEntry,
  folderContentsToSpots,
  formatNameWithFolderName,
  formatPreview,
  formatUploadDate,
  getArchiveStatus,
  getCreativeCategories,
  getFolderUrl,
  getFriendlyType,
  getMediaEntries,
  isContentFolder,
  isImage,
  isUserContent,
  isValidMimeType,
  isVideo,
  MEDIA_ENTRY_SHAPE,
  normalizeContentGroup,
  sortByUploadDate,
  strategyLevelAppToMediaEntry,
  strategyLevelCGToMediaEntry,
  strategyLevelUCToMediaEntry,
  strategyLevelWebpageToMediaEntry,
  userContentToMediaEntry,
  webPageToMediaEntry,
};
